import { useEffect, useMemo, useState } from "react"
import { PanelWrapper, FormField, Data, ModalId, useUIActions, FieldType, useGetCrumbs, O, FieldValue, ValidationModal2 } from "../../../../../features/ui"
import { Budget, Customer } from "../../../state/types"
import { useAdministrationState } from "../../../../administration"
import { TableName } from "../../../../../tables"
import { useActions } from "./actions"
import { Data2, DataTypeForm2, QuickFormModal } from "../../../../../features/data-types"
import { Product } from "../../../../products"
import { PriceBreakdown, usePricesHooks, OperationItemFormModal, OperationItemsTable, usePriceOutlineHooks, useOperationHooks, ItemValidation, operationHelpers } from '../../../../operations'
import { BudgetPriceList } from "../budget-price-list/budgetPriceList"
import { BudgetPriceItem } from "../../../hooks/types"
import { useNavigate } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { SellerField } from "../../seller-field/sellerField"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { CustomerField } from "../../customer/customer-field/customerField"
import { CustomerQuickForm } from "../../customer/customer-quick-form/customerQuickForm"
import { CustomerWarningModal } from "../../customer/customer-warning-modal/customerWarningModal"
import { useCustomerService } from "../../../services/customerService"
import { useBudgetHooks } from "../../../hooks/budgetHooks"

export const BudgetForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const budget = dataType as O<Budget>
    const createMode = !budget

    const { currentEmployee } = useAdministrationState()

    const pricesHooks = usePricesHooks()

    const defaultItemsData = (budget?.items || []).map(item => ({
        ...item,
        id: undefined,
        price: pricesHooks.product.basePrice(item.productId),
        customerTypeYield: undefined,
        vatTaxYield: undefined
    }))

    const [sellerId, setSellerId] = useState<O<string>>(currentEmployee?.id)
    const [customerId, setCustomerId] = useState<O<string>>(budget?.customerId)
    const [customerTypeId, setCustomerTypeId] = useState<O<string>>(budget?.customerTypeId)
    const [itemsData, setItemsData] = useState<Data2[]>(defaultItemsData)
    const [currentItem, setCurrentItem] = useState<Data2>()
    const [priceItems, setPriceItems] = useState<BudgetPriceItem[]>([])

    const { describeCode } = operationHelpers
    const { getBudgetPricesList } = useBudgetHooks()
    const { validateItems } = useOperationHooks()
    const { getPriceOutlineLive } = usePriceOutlineHooks()
    const navigate = useNavigate()

    const customerLoaders = useCustomerLoaders()

    const { submitBudget } = useActions()
    const { openModal } = useUIActions()
    const { quickCreateCustomer } = useCustomerService()

    const priceOutline = useMemo(() => getPriceOutlineLive(itemsData, customerTypeId), [itemsData, customerTypeId])

    useEffect(() => {
        setPriceItems(getBudgetPricesList(priceOutline.total))
    }, [priceOutline])

    const codeLabel = budget && describeCode(budget)
    const title = `${createMode ? 'Crear' : 'Actualizar'} Presupuesto${!createMode ? ' ' + codeLabel : ''}`
    const crumbs = useGetCrumbs(
        'Presupuestos',
        RoutePath.BUDGETS,
        codeLabel,
        budget?.id,
        createMode ? 'Crear' : 'Actualizar'
    )

    const filterProducts = (product: Product) =>
        currentItem?.productId === product.id ||
        (pricesHooks.product.isForSale(product.id) && !itemsData.map(itemData => itemData.productId).includes(product.id))

    const openFormModal = () => openModal(ModalId.OPERATION_ITEM_FORM)

    const onChangeSeller = (value?: FieldValue) => setSellerId(value as O<string>)
    
    const onChangeCustomer = async (value?: FieldValue) => {
        const { loadCustomer } = customerLoaders()
        const newCustomer = value ? await loadCustomer(value as string) : undefined
        setCustomerId(newCustomer?.id)
        setCustomerTypeId(newCustomer?.customerTypeId)
    }

    const openQuickForm = () => openModal(ModalId.QUICK_FORM)

    const onChangeCustomerType = (value?: FieldValue) => setCustomerTypeId(value as O<string>)

    const onCreateItem = () => {
        setCurrentItem(undefined)
        openFormModal()
    }

    const onEditItem = (productId: string) => {
        setCurrentItem(itemsData.find(itemData => itemData.productId === productId))
        openFormModal()
    }

    const onRemoveItem = (productId: string) => {
        const newItemsData = itemsData.filter(itemData => itemData.productId !== productId)
        setItemsData(newItemsData)
    }

    const fields: FormField[] = [
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha',
            defaultValue: new Date(),
            disabled: true
        },
        SellerField({
            defaultValue: sellerId,
            narrow: true,
            active: createMode,
            onChange: onChangeSeller
        }),
        CustomerField({
            value: customerId,
            showAddress: true,
            narrow: true,
            active: createMode,
            onChange: onChangeCustomer,
            onCreateNew: openQuickForm
        }),
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            value: customerTypeId,
            label: 'Lista de Precios',
            narrow: true,
            onChange: onChangeCustomerType
        },
        {
            name: 'budgetItems',
            type: FieldType.TABLE,
            render: () => (
                <OperationItemsTable
                    itemsData={itemsData}
                    createMode
                    customerTypeId={customerTypeId}
                    onCreateItem={onCreateItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                />
            )
        },
        {
            name: 'priceBreakdown',
            type: FieldType.PRICE,
            label: 'Precio',
            render: () => <PriceBreakdown data={priceOutline} />
        }
    ]
    if (priceItems.length > 0 && priceOutline.total !== 0) {
        fields.push({
            name: 'priceList',
            type: FieldType.TABLE,
            label: 'Medios de Pago',
            render: () => <BudgetPriceList priceItems={priceItems} collapsable />
        })
    }

    const validate = async (): Promise<string[]> => {
        // Updating a budget saves a new one: it can't carry products that lost their price
        return validateItems(itemsData, [ItemValidation.NOT_EMPTY, ItemValidation.FOR_SALE])
    }

    const onSubmit = async (budgetData: Data) => {
        await submitBudget(
            budgetData,
            sellerId!,
            customerId!,
            itemsData
        )
    }

    const onCancel = () => navigate(-1)

    const onSaveItem = (newData: Data2) => {
        const matches = (data_1: Data2, data_2: Data2) => data_1.productId === data_2.productId
        const newItemsData = currentItem ?
            itemsData.map(itemData => matches(itemData, currentItem) ? newData : itemData) :
            itemsData.find(itemData => matches(itemData, newData)) ? itemsData : [...itemsData, newData]
        setItemsData(newItemsData)
    }

    const onSaveCustomer = (savedCustomer: Customer) => setCustomerId(savedCustomer.id)

    const onConfirmSubmitCustomer = async () => {
        const savedCustomer = await quickCreateCustomer()
        onSaveCustomer(savedCustomer)
    }

    return (
        <PanelWrapper
            title={title}
            crumbs={crumbs}
            wide
        >
            <DataTypeForm2
               formId="budget-form"
               fields={fields}
               createMode
               createLabel={`Crear${!createMode ? ' Nuevo' : ''}`}
               onSubmit={onSubmit}
               onCancel={onCancel}
               validate={validate}
            />
            <OperationItemFormModal
                defaultData={currentItem}
                createMode
                customerTypeId={customerTypeId}
                defaultPrice={pricesHooks.product.basePrice}
                filterProducts={filterProducts}
                onSubmit={onSaveItem}
            />
            <QuickFormModal title="Crear Cliente" widthSize="l">
                <CustomerQuickForm onSubmit={onSaveCustomer} />
            </QuickFormModal>
            <CustomerWarningModal
                onSubmit={onConfirmSubmitCustomer}
                onCancel={openQuickForm}
            />
            <ValidationModal2 title="Validaciones" />
        </PanelWrapper>
    )
}
